import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatBytes } from '../ui/format';
import { EmptyState, ErrorState, Loading, PageHeader } from '../ui/Page';
import { Menu } from '../ui/Menu';
import { FileDetails } from './FileDetails';
import { SharePanel } from './SharePanel';
import { FileCard, kindOf } from './FileCard';
import {
  createFolder,
  deleteFile,
  deleteFolder,
  getLibrary,
  listFiles,
  restoreFile,
  setFavourite,
  uploadFile,
  type FolderRow,
  type Shelf,
} from './api';

/**
 * The media library (context.md §60–§65). Redrawn in Phase 145.
 *
 * What it was: a list of folder buttons, a grid of filenames, a quota bar at the top. Everything
 * the product knows was on screen and nothing was *findable* — an avatar and a voice note and a
 * contract looked identical, because a file was a row of text.
 *
 * What changed, and why each one:
 *
 * - **Shelves before folders.** "Recentes", "compartilhados", "favoritos" and the bin are how
 *   people look for a file they had last week; a folder is how they file one they are keeping.
 * - **Files show themselves.** An image is its own best label, and the signed URL behind it is
 *   minted only when the card scrolls into view (`FileCard`).
 * - **The bin exists.** `deleted_at` has been on the table since the first storage migration and
 *   nothing could look at it, so deleting was a one-way door with no sign on it.
 * - **Counts are the server's.** A folder showing "0" because its contents are on page two is
 *   worse than no number at all.
 */

const CATEGORIES: { key: string; prefix: string }[] = [
  { key: 'image', prefix: 'image/' },
  { key: 'video', prefix: 'video/' },
  { key: 'audio', prefix: 'audio/' },
  { key: 'pdf', prefix: 'application/pdf' },
  { key: 'text', prefix: 'text/' },
];

const SHELVES: { key: Shelf; icon: string }[] = [
  { key: 'ALL', icon: '▤' },
  { key: 'RECENT', icon: '◷' },
  { key: 'SHARED', icon: '↗' },
  { key: 'FAVOURITES', icon: '★' },
];

interface UploadState {
  name: string;
  status: 'sending' | 'done' | 'failed';
  error?: string;
}

export function FilesPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const picker = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [shelf, setShelf] = useState<Shelf>('ALL');
  const [folderId, setFolderId] = useState('');
  const [sharingFolder, setSharingFolder] = useState<FolderRow | null>(null);
  const [category, setCategory] = useState('');
  const [tag, setTag] = useState('');
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [uploads, setUploads] = useState<UploadState[]>([]);
  const [dragging, setDragging] = useState(false);
  const [newFolder, setNewFolder] = useState('');
  const [addingFolder, setAddingFolder] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const library = useQuery({ queryKey: ['library'], queryFn: getLibrary });

  const files = useQuery({
    queryKey: ['files', applied, shelf, folderId, category, tag],
    queryFn: () =>
      listFiles({
        ...(applied ? { search: applied } : {}),
        ...(shelf !== 'ALL' ? { shelf } : {}),
        ...(folderId === 'root' ? { root: true } : folderId ? { folderId } : {}),
        ...(category ? { mimePrefix: category } : {}),
        ...(tag ? { tags: [tag] } : {}),
      }),
  });

  const rows = useMemo(() => files.data?.files ?? [], [files.data?.files]);
  const selected = rows.find((file) => file.id === selectedId) ?? null;
  const folders = library.data?.folders ?? [];
  const counts = library.data?.counts;
  const quota = library.data?.usage;

  const folderName = (id: string | null): string | null =>
    id ? (folders.find((folder) => folder.id === id)?.name ?? null) : null;

  const knownTags = useMemo(() => {
    const set = new Set<string>();

    for (const file of rows) for (const each of file.tags) set.add(each);

    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows]);

  /* The counts on the type tabs describe what is loaded, and say so by being beside it. */
  const byKind = useMemo(() => {
    const tally = new Map<string, number>();

    for (const file of rows) {
      const kind = kindOf(file.mimeType);
      tally.set(kind, (tally.get(kind) ?? 0) + 1);
    }

    return tally;
  }, [rows]);

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['files'] });
    void queryClient.invalidateQueries({ queryKey: ['library'] });
  };

  const addFolder = useMutation({
    mutationFn: () =>
      createFolder({
        name: newFolder.trim(),
        ...(folderId && folderId !== 'root' ? { parentId: folderId } : {}),
      }),
    onSuccess: () => {
      setNewFolder('');
      setAddingFolder(false);
      setError(null);
      refresh();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const dropFolder = useMutation({
    mutationFn: (id: string) => deleteFolder(id),
    onSuccess: (_result, id) => {
      if (folderId === id) setFolderId('');
      setError(null);
      refresh();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const star = useMutation({
    mutationFn: (input: { id: string; wanted: boolean }) => setFavourite(input.id, input.wanted),
    onSuccess: refresh,
    onError: (caught) => setError(messageFor(caught)),
  });

  const remove = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await deleteFile(id);
    },
    onSuccess: () => {
      setChecked(new Set());
      setSelectedId(null);
      setError(null);
      refresh();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const restore = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await restoreFile(id);
    },
    onSuccess: () => {
      setChecked(new Set());
      setError(null);
      refresh();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  async function send(list: FileList | null): Promise<void> {
    if (!list || list.length === 0) return;

    const chosen = [...list];

    setUploads(chosen.map((file) => ({ name: file.name, status: 'sending' as const })));

    // One at a time: the quota is checked when the ticket is issued, and sending in parallel would
    // let a tenant reserve past its limit before the first reservation lands.
    for (const [index, file] of chosen.entries()) {
      try {
        await uploadFile(file, folderId && folderId !== 'root' ? { folderId } : {});

        setUploads((current) =>
          current.map((each, position) =>
            position === index ? { ...each, status: 'done' } : each,
          ),
        );
      } catch (caught) {
        setUploads((current) =>
          current.map((each, position) =>
            position === index ? { ...each, status: 'failed', error: messageFor(caught) } : each,
          ),
        );
      }
    }

    refresh();
  }

  const percent =
    quota && quota.quotaBytes ? Math.min(100, (quota.usedBytes / quota.quotaBytes) * 100) : null;

  const where = folderId && folderId !== 'root' ? folderName(folderId) : null;
  const heading =
    where ?? (folderId === 'root' ? t('files.rootFolder') : t(`files.shelf.${shelf}`));

  const toggle = (id: string, next: boolean): void => {
    setChecked((current) => {
      const copy = new Set(current);

      if (next) copy.add(id);
      else copy.delete(id);

      return copy;
    });
  };

  const goTo = (next: Shelf): void => {
    setShelf(next);
    setFolderId('');
    setChecked(new Set());
    setSelectedId(null);
  };

  return (
    <div className="view view--wide files-view">
      <PageHeader
        title={t('nav.files')}
        description={t('files.description')}
        actions={
          <span className="cell-inline">
            <button
              type="button"
              className="button button--quiet"
              onClick={() => setAddingFolder((current) => !current)}
            >
              + {t('files.newFolder')}
            </button>

            <button
              type="button"
              className="button button--primary"
              onClick={() => picker.current?.click()}
            >
              {t('files.upload')}
            </button>
          </span>
        }
      />

      <input
        ref={picker}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          void send(event.target.files);
          event.target.value = '';
        }}
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {uploads.length > 0 && (
        <ul className="uploads">
          {uploads.map((upload) => (
            <li key={upload.name} className={`uploads__item uploads__item--${upload.status}`}>
              <span>{upload.name}</span>
              <span className="muted small">
                {upload.status === 'failed'
                  ? (upload.error ?? t('files.uploadFailed'))
                  : t(`files.upload.${upload.status}`)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="library">
        {/* ----- the shelves, the tree, and what is left of the quota ----- */}
        <nav className="shelf" aria-label={t('files.folders')}>
          <ul className="shelf__list">
            {SHELVES.map((entry) => (
              <li key={entry.key}>
                <button
                  type="button"
                  className={`shelf__item${shelf === entry.key && !folderId ? ' is-active' : ''}`}
                  onClick={() => goTo(entry.key)}
                >
                  <span className="shelf__icon" aria-hidden="true">
                    {entry.icon}
                  </span>
                  <span className="shelf__label">{t(`files.shelf.${entry.key}`)}</span>
                  <span className="shelf__count">{countFor(counts, entry.key)}</span>
                </button>
              </li>
            ))}
          </ul>

          <div className="shelf__group">
            <span className="shelf__group-title">{t('files.folders')}</span>
            <button
              type="button"
              className="button button--quiet button--compact"
              aria-label={t('files.newFolder')}
              onClick={() => setAddingFolder((current) => !current)}
            >
              +
            </button>
          </div>

          {addingFolder && (
            <form
              className="shelf__new"
              onSubmit={(event) => {
                event.preventDefault();
                if (newFolder.trim()) addFolder.mutate();
              }}
            >
              <input
                className="input"
                autoFocus
                value={newFolder}
                placeholder={t('files.newFolder')}
                maxLength={120}
                onChange={(event) => setNewFolder(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setAddingFolder(false);
                }}
              />
              <button
                type="submit"
                className="button button--primary button--compact"
                disabled={!newFolder.trim() || addFolder.isPending}
              >
                {t('common.save')}
              </button>
            </form>
          )}

          <ul className="shelf__list">
            <li>
              <button
                type="button"
                className={`shelf__item${folderId === 'root' ? ' is-active' : ''}`}
                onClick={() => {
                  setShelf('ALL');
                  setFolderId('root');
                }}
              >
                <span className="shelf__icon" aria-hidden="true">
                  ▢
                </span>
                <span className="shelf__label">{t('files.rootFolder')}</span>
              </button>
            </li>

            {folders.map((folder) => (
              <li key={folder.id}>
                <span
                  className="shelf__folder"
                  style={{ paddingInlineStart: `${depthOf(folder, folders) * 14}px` }}
                >
                  <button
                    type="button"
                    className={`shelf__item${folderId === folder.id ? ' is-active' : ''}`}
                    onClick={() => {
                      setShelf('ALL');
                      setFolderId(folder.id);
                    }}
                  >
                    <span className="shelf__icon" aria-hidden="true">
                      ▤
                    </span>
                    <span className="shelf__label">{folder.name}</span>
                    <span className="shelf__count">{folder.file_count ?? 0}</span>
                  </button>

                  <Menu label={t('files.folderActions')}>
                    <button type="button" onClick={() => setSharingFolder(folder)}>
                      {t('files.share')}
                    </button>

                    {/* The product's own folders cannot be deleted; saying so beats a refusal. */}
                    {folder.system_key ? (
                      <span className="muted small">{t('files.maintained')}</span>
                    ) : (
                      <button type="button" onClick={() => dropFolder.mutate(folder.id)}>
                        {t('files.deleteConfirm')}
                      </button>
                    )}
                  </Menu>
                </span>
              </li>
            ))}
          </ul>

          <ul className="shelf__list shelf__list--end">
            <li>
              <button
                type="button"
                className={`shelf__item${shelf === 'TRASH' ? ' is-active' : ''}`}
                onClick={() => goTo('TRASH')}
              >
                <span className="shelf__icon" aria-hidden="true">
                  ⌫
                </span>
                <span className="shelf__label">{t('files.shelf.TRASH')}</span>
                <span className="shelf__count">{counts?.trashed ?? 0}</span>
              </button>
            </li>
          </ul>

          {quota && (
            <div className="shelf__quota">
              <span className="strong">{t('files.storage')}</span>
              <span className="muted small">
                {formatBytes(quota.usedBytes)}
                {quota.quotaBytes ? ` ${t('files.ofQuota')} ${formatBytes(quota.quotaBytes)}` : ''}
              </span>

              {percent !== null && (
                <div
                  className="quota__bar"
                  role="progressbar"
                  aria-valuenow={Math.round(percent)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <span
                    style={{ width: `${percent}%` }}
                    data-full={percent > 90 ? 'true' : undefined}
                  />
                </div>
              )}
            </div>
          )}
        </nav>

        {/* ----- the files themselves ----- */}
        <section className="library__main">
          <form
            className="library__toolbar"
            onSubmit={(event) => {
              event.preventDefault();
              setApplied(search.trim());
            }}
          >
            <input
              className="input library__search"
              value={search}
              placeholder={t('files.search')}
              onChange={(event) => setSearch(event.target.value)}
            />

            <select
              className="select"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option value="">{t('files.allTypes')}</option>
              {CATEGORIES.map((entry) => (
                <option key={entry.key} value={entry.prefix}>
                  {t(`files.cat.${entry.key}`)}
                </option>
              ))}
            </select>

            <select className="select" value={tag} onChange={(event) => setTag(event.target.value)}>
              <option value="">{t('files.allTags')}</option>
              {knownTags.map((each) => (
                <option key={each} value={each}>
                  {each}
                </option>
              ))}
            </select>

            <button type="submit" className="button button--quiet">
              {t('files.searchAction')}
            </button>
          </form>

          <div className="library__heading">
            <span className="cell-stack">
              <span className="muted small">
                {t('nav.files')} / {heading}
              </span>
              <span className="cell-inline">
                <h2>{heading}</h2>
                <span className="muted small">
                  {rows.length} {t('files.fileCount')}
                </span>
              </span>
            </span>

            <div className="cell-inline">
              {/* Two shapes for the same list: a grid to recognise a picture, a list to compare
                  names and dates. */}
              <div className="chips" role="group" aria-label={t('files.layout')}>
                <button
                  type="button"
                  className={`chip${layout === 'grid' ? ' chip--active' : ''}`}
                  aria-pressed={layout === 'grid'}
                  onClick={() => setLayout('grid')}
                >
                  ▦
                </button>
                <button
                  type="button"
                  className={`chip${layout === 'list' ? ' chip--active' : ''}`}
                  aria-pressed={layout === 'list'}
                  onClick={() => setLayout('list')}
                >
                  ☰
                </button>
              </div>
            </div>
          </div>

          {/* The kinds present in what is loaded, as a quick filter over it. */}
          <div className="chips library__kinds">
            <button
              type="button"
              className={`chip${category === '' ? ' chip--active' : ''}`}
              onClick={() => setCategory('')}
            >
              {t('files.allTypes')} <span className="muted">{rows.length}</span>
            </button>

            {CATEGORIES.filter((entry) => (byKind.get(entry.key) ?? 0) > 0).map((entry) => (
              <button
                key={entry.key}
                type="button"
                className={`chip${category === entry.prefix ? ' chip--active' : ''}`}
                onClick={() => setCategory(entry.prefix)}
              >
                {t(`files.cat.${entry.key}`)}{' '}
                <span className="muted">{byKind.get(entry.key) ?? 0}</span>
              </button>
            ))}
          </div>

          {checked.size > 0 && (
            <div className="library__bulk">
              <span className="strong">
                {t('files.selected').replace('{n}', String(checked.size))}
              </span>

              {shelf === 'TRASH' ? (
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={restore.isPending}
                  onClick={() => restore.mutate([...checked])}
                >
                  {t('files.restore')}
                </button>
              ) : (
                <button
                  type="button"
                  className="button button--danger button--compact"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate([...checked])}
                >
                  {t('files.deleteConfirm')}
                </button>
              )}

              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setChecked(new Set())}
              >
                {t('confirm.cancel')}
              </button>
            </div>
          )}

          <div
            className={`library__drop ${dragging ? 'is-dragging' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              void send(event.dataTransfer.files);
            }}
          >
            {files.isLoading || library.isLoading ? (
              <Loading />
            ) : files.error ? (
              <ErrorState error={files.error} onRetry={() => void files.refetch()} />
            ) : rows.length === 0 ? (
              <EmptyState
                title={shelf === 'TRASH' ? t('files.emptyTrash') : t('files.empty')}
                description={shelf === 'TRASH' ? undefined : t('files.dropHint')}
              />
            ) : layout === 'grid' ? (
              <div className="file-grid">
                {rows.map((file) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    folderName={folderName(file.folderId)}
                    selected={checked.has(file.id)}
                    onSelect={(next) => toggle(file.id, next)}
                    onOpen={() => setSelectedId(file.id)}
                    onToggleFavourite={() => star.mutate({ id: file.id, wanted: !file.favourite })}
                    menu={
                      <CardMenu
                        shelf={shelf}
                        onOpen={() => setSelectedId(file.id)}
                        onRemove={() => remove.mutate([file.id])}
                        onRestore={() => restore.mutate([file.id])}
                      />
                    }
                  />
                ))}
              </div>
            ) : (
              <ul className="file-rows">
                {rows.map((file) => (
                  <li key={file.id}>
                    <label className="file-rows__check">
                      <input
                        type="checkbox"
                        checked={checked.has(file.id)}
                        aria-label={t('files.select').replace('{name}', file.name)}
                        onChange={(event) => toggle(file.id, event.target.checked)}
                      />
                    </label>

                    <button
                      type="button"
                      className="file-rows__name"
                      onClick={() => setSelectedId(file.id)}
                    >
                      {file.name}
                    </button>

                    <span className="muted small">
                      {folderName(file.folderId) ?? t('files.loose')}
                    </span>
                    <span className="muted small">{formatBytes(file.sizeBytes)}</span>

                    <CardMenu
                      shelf={shelf}
                      onOpen={() => setSelectedId(file.id)}
                      onRemove={() => remove.mutate([file.id])}
                      onRestore={() => restore.mutate([file.id])}
                    />
                  </li>
                ))}
              </ul>
            )}

            <p className="library__dropnote muted small">
              {t('files.dropHint')}
              {quota?.quotaBytes ? ` · ${formatBytes(quota.quotaBytes)}` : ''}
            </p>
          </div>
        </section>

        {sharingFolder && (
          <SharePanel
            subject={{ kind: 'FOLDER', id: sharingFolder.id, name: sharingFolder.name }}
            onClose={() => setSharingFolder(null)}
          />
        )}

        {selected && !sharingFolder && (
          <FileDetails file={selected} folders={folders} onClose={() => setSelectedId(null)} />
        )}
      </div>
    </div>
  );
}

function CardMenu({
  shelf,
  onOpen,
  onRemove,
  onRestore,
}: {
  shelf: Shelf;
  onOpen: () => void;
  onRemove: () => void;
  onRestore: () => void;
}): React.JSX.Element {
  return (
    <Menu label={t('files.fileActions')}>
      <button type="button" onClick={onOpen}>
        {t('files.details')}
      </button>

      {shelf === 'TRASH' ? (
        <button type="button" onClick={onRestore}>
          {t('files.restore')}
        </button>
      ) : (
        <button type="button" onClick={onRemove}>
          {t('files.deleteConfirm')}
        </button>
      )}
    </Menu>
  );
}

function countFor(
  counts: { all: number; recent: number; shared: number; favourites: number } | undefined,
  shelf: Shelf,
): number {
  if (!counts) return 0;
  if (shelf === 'RECENT') return counts.recent;
  if (shelf === 'SHARED') return counts.shared;
  if (shelf === 'FAVOURITES') return counts.favourites;

  return counts.all;
}

function depthOf(folder: FolderRow, all: FolderRow[]): number {
  let depth = 0;
  let current = folder;

  // Bounded by the list length: a cycle would otherwise loop forever, and the database does not
  // promise there is none.
  while (current.parent_id && depth < all.length) {
    const parent = all.find((each) => each.id === current.parent_id);

    if (!parent) break;

    current = parent;
    depth += 1;
  }

  return depth;
}
